import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * App-wide "which equipment does this user have pinned?" cache so the star
 * icon renders the correct state everywhere (Dashboard strip, Location
 * Equipment cards, Machinery Detail page) and updates atomically when the
 * user clicks a star anywhere.
 *
 * Store only the id set here; pages that need FULL machinery records for
 * the pins (Dashboard strip) fetch their own richer payload from
 * `/api/equipment-pins`.
 */
const PinsContext = createContext({
  pinnedIds: new Set(),
  setPinnedFromArray: () => {},
  refresh: async () => {},
});

export const PinsProvider = ({ children, userId }) => {
  const [pinnedIds, setPinnedIds] = useState(new Set());

  const setPinnedFromArray = useCallback((arr) => {
    setPinnedIds(new Set(arr || []));
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setPinnedFromArray([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/equipment-pins`);
      setPinnedFromArray(res.data.map((m) => m.id));
    } catch (e) {
      // Silent — the star will just render un-pinned until refresh succeeds.
    }
  }, [setPinnedFromArray, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <PinsContext.Provider value={{ pinnedIds, setPinnedFromArray, refresh }}>
      {children}
    </PinsContext.Provider>
  );
};

export const usePins = () => useContext(PinsContext);
